/**
 * Data layer for `cap-hit-auto-pause`. Thin typed fetch wrappers over /api/v1/cap-hit-auto-pause.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CapHitAutoPauseRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCapHitAutoPauseResponse {
	count: number;
	items: CapHitAutoPauseRow[];
}

export interface CreateCapHitAutoPauseBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/cap-hit-auto-pause";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCapHitAutoPauseResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCapHitAutoPauseResponse>(res);
}

export async function create(body: CreateCapHitAutoPauseBody): Promise<CapHitAutoPauseRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CapHitAutoPauseRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const capHitAutoPauseQueryKeys = {
	all: ["cap-hit-auto-pause"] as const,
	list: () => [...capHitAutoPauseQueryKeys.all] as const,
	detail: (id: string) => [...capHitAutoPauseQueryKeys.all, id] as const,
};
